/**
 * RenamePortfolioModal — rename an aggregation / portfolio (SHA-43 portfolio CRUD).
 *
 * Uses the existing PUT /api/aggregations/:id endpoint (name field). Mirrors
 * RenameStrategyModal but operates on aggregations, not user strategies.
 */

import { useEffect, useState } from 'react';
import { Modal } from '../Modal/Modal.js';
import { useUpdateAggregation } from '../../hooks/useBacktest.js';

interface RenamePortfolioModalProps {
  isOpen: boolean;
  portfolioId: string;
  currentName: string;
  onClose: () => void;
}

export function RenamePortfolioModal({
  isOpen,
  portfolioId,
  currentName,
  onClose,
}: RenamePortfolioModalProps) {
  const [name, setName] = useState(currentName);
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync, isPending } = useUpdateAggregation();

  // Reset local state whenever the modal is (re)opened for a portfolio.
  useEffect(() => {
    if (isOpen) {
      setName(currentName);
      setError(null);
    }
  }, [isOpen, portfolioId, currentName]);

  const trimmed = name.trim();
  const canSave = trimmed.length > 0 && trimmed !== currentName && !isPending;

  const handleSave = async () => {
    if (!canSave) return;
    setError(null);
    try {
      await mutateAsync({ id: portfolioId, updates: { name: trimmed } });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename portfolio');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Rename Portfolio" size="md">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label htmlFor="rename-portfolio-input" style={{ fontSize: 12, color: '#999' }}>
          New name
        </label>
        <input
          id="rename-portfolio-input"
          aria-label="New name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleSave();
          }}
          autoFocus
          style={{
            width: '100%',
            padding: '8px 10px',
            background: '#252525',
            border: '1px solid #444',
            borderRadius: 6,
            color: '#e0e0e0',
            fontSize: 13,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />

        {error && (
          <div style={{ fontSize: 12.5, color: '#f87171' }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
          <button
            onClick={onClose}
            style={{
              padding: '7px 14px',
              background: 'none',
              border: '1px solid #444',
              borderRadius: 6,
              color: '#aaa',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => void handleSave()}
            disabled={!canSave}
            style={{
              padding: '7px 16px',
              background: canSave ? '#2196f3' : '#333',
              border: 'none',
              borderRadius: 6,
              color: canSave ? '#fff' : '#666',
              fontSize: 13,
              fontWeight: 600,
              cursor: canSave ? 'pointer' : 'not-allowed',
            }}
          >
            {isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
